import React, { memo, useCallback, Fragment, useEffect, useMemo } from 'react';
import { ProcessBlockVariation, ProcessForm, StepForm } from './ProcessBlock';
import { useObservable } from '@core/utils/hooks/rxjs';
import { Label, Row, Col } from 'reactstrap';
import { FormInput } from '@core/components/form';
import { SortableList } from '@modules/common/Sortable';
import { SortableElement, SortableHandle, SortEnd } from 'react-sortable-hoc';
import { R } from '@core/utils/r';
import { FormHTML } from '@core/components/form/FormHTML';

export interface ScenarioDrillBlockSettingsProps {
  form: ProcessForm;
  variation: ProcessBlockVariation;
}

export const ProcessBlockSettings = memo((props: ScenarioDrillBlockSettingsProps) => {
  const { form, variation } = props;
  const steps = useObservable(form.controls.steps.items$, []);
  const stepValues = useObservable(form.controls.steps.value$, []);

  useEffect(() => {
    if (steps.length === 0) {
      form.controls.steps.push();
    }
  }, [form, steps]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newStep = R.move(stepValues, oldIndex, newIndex);
      form.patchValue({
        steps: newStep,
      });
    },
    [form, stepValues],
  );
  const totalSteps = useMemo(() => steps.length, [steps.length]);
  return (
    <div>
      <Fragment>
        <Row>
          <Col md={9}>
            <Label>Process Title</Label>
            <FormInput control={form.controls.processTitle} />
            <Label>Introduction</Label>
            <FormInput control={form.controls.introTitle} />
            <FormHTML control={form.controls.introDetails} minimal={true} />
          </Col>
        </Row>
        <hr />
        {!!steps.length && <Label>Steps</Label>}
        <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSortEnd(sort)} useDragHandle={true} helperClass="z5000">
          {steps.map((ctrl, index) => {
            return <SortableProcessItem key={index} index={index} ctrl={ctrl} form={form} i={index} totalSteps={totalSteps} />;
          })}
        </SortableList>
        <Row>
          <Col md={9}>
            <Label>Summary</Label>
            <FormInput control={form.controls.summaryTitle} />
            <FormHTML control={form.controls.summaryDetails} minimal={true} />
          </Col>
        </Row>
      </Fragment>
    </div>
  );
});

interface ScenarioItemProps {
  i: number;
  ctrl: StepForm;
  form: ProcessForm;
  totalSteps: number;
}

const ProcessItemSettings = memo((props: ScenarioItemProps) => {
  const { ctrl, form, i, totalSteps } = props;

  const DragHandle = SortableHandle(() => <i className="fas fa-grip-vertical font-size-16 mr-3 mb-3" style={{ cursor: 'move' }}></i>);
  const showAddStepBtn = useMemo(() => totalSteps - 1 == i, [i, totalSteps]);

  return (
    <div>
      <label className="text-muted" style={{ opacity: 0.5 }}>
        Step #{i + 1}
      </label>
      <Row key={`step-${i} mb-2 mt-2`}>
        <Col md={10} className="input-width">
          <DragHandle />
          <FormInput control={ctrl.controls.stepTitle} />
        </Col>
        <Col md={2}>
          {!!i && (
            <a className="btn btn-outline-danger btn-sm mr-2" onClick={() => form.controls.steps.removeAt(i)}>
              <i className="bx bx-x"></i>
            </a>
          )}
        </Col>
        <Col md={12}>
          <FormHTML control={ctrl.controls.stepDetails} minimal={true} />
        </Col>
        <Col md={12}>
          {showAddStepBtn && (
            <a className="btn btn-outline-primary btn-sm mr-2" onClick={() => form.controls.steps.push()}>
              Add Step
            </a>
          )}
        </Col>
      </Row>
      <hr />
    </div>
  );
});

export const SortableProcessItem = SortableElement(ProcessItemSettings);
